<template>
  <modal :show="showModal" modal-classes="modal-secondary" size="lg">
    <h3 class="mb-1">{{ title }}</h3>
    <p class="text-muted">{{ help }}</p>

    <base-input :label="`${$t('INTERNAL_DOCUMENTS.REASON')} (*)`">
      <el-select
        v-model="reasonCode"
        :placeholder="$t('INTERNAL_DOCUMENTS.REASON_PLACEHOLDER')"
        class="w-100"
      >
        <el-option
          v-for="code in reasons"
          :key="code"
          :value="code"
          :label="$t(`INTERNAL_DOCUMENTS.${reasonsKey}.${code}`)"
        />
      </el-select>
    </base-input>

    <base-input :label="detailLabel">
      <textarea
        v-model="reason"
        class="form-control"
        rows="3"
        :placeholder="$t('INTERNAL_DOCUMENTS.REASON_DETAIL_PLACEHOLDER')"
      ></textarea>
    </base-input>

    <p v-if="detailRequired" class="text-muted small">
      {{ $t("INTERNAL_DOCUMENTS.REASON_DETAIL_REQUIRED_HELP") }}
    </p>

    <template slot="footer">
      <button
        type="button"
        class="btn btn-danger"
        :disabled="loading || !canConfirm"
        @click="confirm"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ confirmLabel || $t("INTERNAL_DOCUMENTS.CONFIRM") }}
      </button>

      <button type="button" class="btn btn-link ml-auto" :disabled="loading" @click="close">
        {{ $t("COMMON.CLOSE") }}
      </button>
    </template>
  </modal>
</template>

<script>
import { Select, Option } from "element-ui";

/**
 * Saisie du motif d'une annulation de facture interne ou d'un refus de commande
 * interne.
 *
 * Ces deux gestes retirent quelque chose à l'autre école — une facture de ses
 * rapports, une semaine de sa production — et sont les seuls du module à lui
 * envoyer un courriel. Le motif y est obligatoire : c'est lui qui dit à l'autre
 * école quoi corriger, et sa forme codée le rend dénombrable.
 */
export default {
  name: "internal-document-reason-modal",

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    help: {
      type: String,
      default: "",
    },
    /** Codes de motif proposés, dans l'ordre d'affichage. */
    reasons: {
      type: Array,
      required: true,
    },
    /** Sous-clé i18n des libellés : CANCELLATION_REASONS ou REJECTION_REASONS. */
    reasonsKey: {
      type: String,
      required: true,
    },
    confirmLabel: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      reasonCode: null,
      reason: null,
    };
  },

  computed: {
    /** « Autre » ne dit rien à lui seul : la précision devient obligatoire. */
    detailRequired() {
      return this.reasonCode === "OTHER";
    },
    detailLabel() {
      const label = this.$t("INTERNAL_DOCUMENTS.REASON_DETAIL");

      return this.detailRequired ? `${label} (*)` : label;
    },
    canConfirm() {
      if (!this.reasonCode) return false;

      return !this.detailRequired || !!(this.reason || "").trim();
    },
  },

  watch: {
    // Rouvrir la fenêtre ne doit jamais proposer le motif du geste précédent.
    showModal(value) {
      if (value) {
        this.reasonCode = null;
        this.reason = null;
      }
    },
  },

  methods: {
    confirm() {
      if (!this.canConfirm) return;

      this.$emit("confirm", {
        reasonCode: this.reasonCode,
        reason: (this.reason || "").trim() || null,
      });
    },
    close() {
      this.$emit("close");
    },
  },
};
</script>
